import React, { useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Check } from 'lucide-react';
interface Order {
  order_id: string;
  closed: string;
  side: 'Buy' | 'Sell';
  type: 'Market' | 'Limit' | 'Stop loss';
  pair: string;
  status: 'Filled' | 'Partially filled' | 'Canceled';
  avg_price: number | null;
  executed_qty: number;
  cost: number | null;
}
const mockOrdersData: Order[] = [{
  order_id: 'OP6J2U',
  closed: '1/18/26 4:43 PM',
  side: 'Sell',
  type: 'Market',
  pair: 'BTC/USD',
  status: 'Canceled',
  avg_price: null,
  executed_qty: 0,
  cost: null
}, {
  order_id: 'OA4PAN',
  closed: '1/18/26 4:43 PM',
  side: 'Sell',
  type: 'Market',
  pair: 'BTC/USD',
  status: 'Filled',
  avg_price: 95078.1,
  executed_qty: 0.0008267,
  cost: 78.6
}, {
  order_id: 'OWY5CU',
  closed: '1/15/26 11:35 PM',
  side: 'Buy',
  type: 'Limit',
  pair: 'BTC/USD',
  status: 'Filled',
  avg_price: 95790.6,
  executed_qty: 0.0008267,
  cost: 79.19
}, {
  order_id: 'OTXWZX',
  closed: '1/15/26 5:59 PM',
  side: 'Buy',
  type: 'Market',
  pair: 'BTC/USD',
  status: 'Canceled',
  avg_price: null,
  executed_qty: 0,
  cost: null
}, {
  order_id: 'OHXQWC',
  closed: '12/12/25 4:18 PM',
  side: 'Sell',
  type: 'Market',
  pair: 'BTC/USD',
  status: 'Filled',
  avg_price: 91772.8,
  executed_qty: 0.00115416,
  cost: 105.92
}];
// Helper to get icon URL
const getIconUrl = (pair: string) => {
  const symbol = pair.split('/')[0].toLowerCase();
  const map: Record<string, string> = {
    btc: 'bitcoin-btc',
    eth: 'ethereum-eth',
    sol: 'solana-sol',
    usdt: 'tether-usdt',
    bnb: 'bnb-bnb',
    xrp: 'xrp-xrp',
    ada: 'cardano-ada',
    doge: 'dogecoin-doge'
  };
  const slug = map[symbol] || `${symbol}-${symbol}`;
  return `https://cryptologos.cc/logos/${slug}-logo.svg`;
};
export function OrdersTab() {
  const [selectedPairs, setSelectedPairs] = useState<string[]>([]);
  const [orderEvent, setOrderEvent] = useState('opened_or_closed');
  const [showPairFilter, setShowPairFilter] = useState(false);
  const [showEventFilter, setShowEventFilter] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const clearFilters = () => {
    setSelectedPairs([]);
    setOrderEvent('opened_or_closed');
    setDateFrom('');
    setDateTo('');
  };
  const togglePair = (pair: string) => {
    if (selectedPairs.includes(pair)) {
      setSelectedPairs(selectedPairs.filter(p => p !== pair));
    } else {
      setSelectedPairs([...selectedPairs, pair]);
    }
  };
  const hasActiveFilters = selectedPairs.length > 0 || orderEvent !== 'opened_or_closed' || dateFrom || dateTo;
  // Filter logic (simplified for mock data)
  const filteredData = mockOrdersData.filter(order => {
    if (selectedPairs.length > 0 && !selectedPairs.includes(order.pair)) return false;
    // Event filter logic would go here
    return true;
  });
  const totalItems = filteredData.length;
  const itemsPerPage = 25;
  const currentPage = 1;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  return <div className="space-y-4" data-id="element-86">
      {/* Filters */}
      <div className="bg-background-secondary border border-border-subtle rounded-xl p-4" data-id="element-87">
        <div className="flex items-center gap-3 flex-wrap" data-id="element-88">
          {/* Pairs Filter */}
          <div className="relative" data-id="element-89">
            <button onClick={() => setShowPairFilter(!showPairFilter)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm ${showPairFilter ? 'bg-background-elevated text-text-primary ring-1 ring-brand-purple' : 'bg-background-elevated hover:bg-background-elevated/80 text-text-primary'}`} data-id="element-90">
              Select pairs
              {selectedPairs.length > 0 && <span className="bg-brand-purple text-white text-[10px] px-1.5 py-0.5 rounded-full" data-id="element-91">
                  {selectedPairs.length}
                </span>}
              <ChevronDown size={16} data-id="element-92" />
            </button>
            {showPairFilter && <>
                <div className="fixed inset-0 z-20" onClick={() => setShowPairFilter(false)} data-id="element-93" />
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-elevated border border-border-subtle rounded-xl shadow-2xl z-30 p-3 max-h-80 overflow-y-auto" data-id="element-94">
                  <div className="space-y-1" data-id="element-95">
                    {['BTC/USD', 'ETH/USD', 'SOL/USD', 'BTC/EUR'].map(pair => <button key={pair} onClick={() => togglePair(pair)} className="w-full flex items-center gap-3 p-2 hover:bg-background-secondary rounded-lg transition-colors text-left group" data-id="element-96">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${selectedPairs.includes(pair) ? 'bg-brand-purple border-brand-purple' : 'border-text-secondary group-hover:border-text-primary'}`} data-id="element-97">
                            {selectedPairs.includes(pair) && <Check size={10} className="text-white" data-id="element-98" />}
                          </div>
                          <span className="text-sm text-text-primary" data-id="element-99">
                            {pair}
                          </span>
                        </button>)}
                  </div>
                </div>
              </>}
          </div>

          {/* Order Event Filter */}
          <div className="relative" data-id="element-100">
            <button onClick={() => setShowEventFilter(!showEventFilter)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm ${showEventFilter ? 'bg-background-elevated text-text-primary ring-1 ring-brand-purple' : 'bg-background-elevated hover:bg-background-elevated/80 text-text-primary'}`} data-id="element-101">
              Order event
              <ChevronDown size={16} data-id="element-102" />
            </button>
            {showEventFilter && <>
                <div className="fixed inset-0 z-20" onClick={() => setShowEventFilter(false)} data-id="element-103" />
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-elevated border border-border-subtle rounded-xl shadow-2xl z-30 p-3" data-id="element-104">
                  <div className="space-y-1" data-id="element-105">
                    {[{
                  value: 'opened_or_closed',
                  label: 'When order was opened or closed'
                }, {
                  value: 'opened_only',
                  label: 'When order was opened only'
                }, {
                  value: 'closed_only',
                  label: 'When order was closed only'
                }].map(option => <button key={option.value} onClick={() => setOrderEvent(option.value)} className="w-full flex items-center justify-between p-2 hover:bg-background-secondary rounded-lg transition-colors text-left group" data-id="element-106">
                        <span className={`text-sm ${orderEvent === option.value ? 'text-text-primary font-medium' : 'text-text-secondary group-hover:text-text-primary'}`} data-id="element-107">
                          {option.label}
                        </span>
                        {orderEvent === option.value && <Check size={16} className="text-brand-purple" data-id="element-108" />}
                      </button>)}
                  </div>
                </div>
              </>}
          </div>

          {/* Date Range */}
          <div className="flex items-center gap-2" data-id="element-109">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="From" className="px-4 py-2 bg-background-elevated text-text-primary rounded-lg text-sm border border-border-subtle focus:border-border-default outline-none" data-id="element-110" />
            <span className="text-text-secondary" data-id="element-111">to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="To" className="px-4 py-2 bg-background-elevated text-text-primary rounded-lg text-sm border border-border-subtle focus:border-border-default outline-none" data-id="element-112" />
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && <button onClick={clearFilters} className="flex items-center gap-2 px-4 py-2 text-text-secondary hover:text-text-primary transition-colors text-sm" data-id="element-113">
              Clear filters
            </button>}
        </div>
      </div>

      {/* Table */}
      <div className="bg-background-secondary border border-border-subtle rounded-xl overflow-hidden" data-id="element-114">
        <div className="overflow-x-auto" data-id="element-115">
          <table className="w-full" data-id="element-116">
            <thead className="bg-background-elevated/50" data-id="element-117">
              <tr className="border-b border-border-subtle" data-id="element-118">
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-119">
                  Closed
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-120">
                  Side
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-121">
                  Type
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-122">
                  Pair
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-123">
                  Status
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-124">
                  Average Price
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-125">
                  Quantity Executed
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-126">
                  Cost
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-127">
                  ID
                </th>
              </tr>
            </thead>
            <tbody data-id="element-128">
              {filteredData.map(order => <tr key={order.order_id} className="border-b border-border-subtle hover:bg-background-elevated/30 transition-colors" data-id="element-129">
                  <td className="px-6 py-4 text-sm text-text-primary" data-id="element-130">
                    {order.closed}
                  </td>
                  <td className="px-6 py-4 text-sm text-text-primary" data-id="element-131">
                    {order.side}
                  </td>
                  <td className="px-6 py-4 text-sm text-text-primary" data-id="element-132">
                    {order.type}
                  </td>
                  <td className="px-6 py-4" data-id="element-133">
                    <div className="flex items-center gap-2" data-id="element-134">
                      <img src={getIconUrl(order.pair)} alt={order.pair} className="w-5 h-5 object-contain" data-id="element-135" />
                      <span className="text-sm font-medium text-text-primary" data-id="element-136">
                        {order.pair}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-137">
                    <span className={`text-xs px-2 py-1 rounded ${order.status === 'Filled' ? 'bg-success/10 text-success' : order.status === 'Partially filled' ? 'bg-warning/10 text-warning' : 'bg-danger/10 text-danger'}`} data-id="element-138">
                      {order.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-139">
                    {order.avg_price ? <div className="font-numbers text-sm text-text-primary" data-id="element-140">
                        {order.avg_price.toLocaleString('en-US', {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1
                  })}
                        <span className="text-text-secondary text-xs ml-1" data-id="element-141">
                          USD
                        </span>
                      </div> : <span className="text-text-secondary text-sm" data-id="element-142">-</span>}
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-143">
                    {order.executed_qty > 0 ? <div className="font-numbers text-sm text-text-primary" data-id="element-144">
                        {order.executed_qty.toFixed(8)}
                        <span className="text-text-secondary text-xs ml-1" data-id="element-145">
                          BTC
                        </span>
                      </div> : <span className="text-text-secondary text-sm" data-id="element-146">-</span>}
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-147">
                    {order.cost ? <div className="font-numbers text-sm text-text-primary" data-id="element-148">
                        {order.cost.toFixed(2)}
                        <span className="text-text-secondary text-xs ml-1" data-id="element-149">
                          USD
                        </span>
                      </div> : <span className="text-text-secondary text-sm" data-id="element-150">-</span>}
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-151">
                    <div className="font-numbers text-xs text-text-secondary" data-id="element-152">
                      {order.order_id}
                    </div>
                  </td>
                </tr>)}
              {filteredData.length === 0 && <tr data-id="element-153">
                  <td colSpan={9} className="px-6 py-8 text-center text-text-secondary" data-id="element-154">
                    No orders found matching your filters.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-border-subtle flex items-center justify-between" data-id="element-155">
          <div className="text-sm text-text-secondary" data-id="element-156">
            {totalItems > 0 ? `1 - ${Math.min(itemsPerPage, totalItems)} of ${totalItems}` : '0 of 0'}
          </div>
          <div className="flex items-center gap-2" data-id="element-157">
            <button disabled={currentPage === 1} className="p-2 text-text-secondary hover:text-text-primary hover:bg-background-elevated rounded-lg transition-all disabled:opacity-50 disabled:hover:bg-transparent" data-id="element-158">
              <ChevronLeft size={16} data-id="element-159" />
            </button>
            <div className="text-sm font-medium text-text-primary px-2" data-id="element-160">
              Page {currentPage} of {totalPages}
            </div>
            <button disabled={currentPage === totalPages} className="p-2 text-text-secondary hover:text-text-primary hover:bg-background-elevated rounded-lg transition-all disabled:opacity-50 disabled:hover:bg-transparent" data-id="element-161">
              <ChevronRight size={16} data-id="element-162" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}